import { ChallengeLocationSummary, ChallengeType, DirectionType, EntityType, GetCommentResponse, TopicCategory } from '../types';
import { GoogleTagEvent, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { css, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { isDefined, isValidString, shouldDisplayAddPointElement, undefinedIfEmpty } from '../helpers/utils';

import { PointCardBase } from './point-card-base';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { heights } from '../style/variables';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import pointCache from '../helpers/state/point-cache';
import { shouldRevealSpecialOffer } from '../helpers/challenge-special-offers';
import topicData from '../helpers/state/topic-data';

@customElement('clue-details-point-card')
export class ClueDetailsPointCard extends PointCardBase {
    static amplitude_callsite: string = 'Clue Details Point Card';

    @query('#image_holder')
    imageHolder!: HTMLImageElement;

    @property({ type: Number })
    challenge_points?: number;

    @state()
    isCheckedin = false;

    @state()
    isPendingApproval = false;

    @state()
    showSpecialOffer: boolean = true;

    @state()
    eventString: string = '';

    @state()
    customFormInputs: Array<{ [key: string]: string }> = [];

    @state()
    currentImageIndex: number = 0;

    @state()
    clueLocation?: ChallengeLocationSummary;

    @state()
    showHint: boolean = false;

    @state()
    allComments: GetCommentResponse[] = [];

    @state()
    showComments: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindChallengeData = new StateController(this, challengeData);

    async connectedCallback() {
        super.connectedCallback();
    }

    async firstUpdated() {
        this.updateCheckinStatus();
    }

    private async commentChanged() {
        this.allComments = await challengeData.getInternalNotesForChallengePoint(this.clueLocation?.point_info?._id.$oid ?? '');
    }

    private onToggleComments() {
        this.showComments = !this.showComments;
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('point')) {
            this.updateCheckinStatus();
            
            // Load internal notes if challenge has internal notes enabled
            if (challengeData.challenge?.allow_internal_notes === true) {
                try {
                    this.allComments = await challengeData.getInternalNotesForChallengePoint(this.clueLocation?.point_info?._id.$oid ?? '');
                } catch (e) {
                    console.error('Error getting comments', e);
                }
            }
        }
        if (changedProperties.has('clueDetails')) {
            this.updateCheckinStatus();
        }
        if (changedProperties.has('clueLocation')) {
            // Pending status derives from challengeLocationId (from clueLocation)
            // so the card must refresh when the clue swaps, otherwise the
            // previous clue's "Awaiting Approval" state can persist.
            this.updateCheckinStatus();
        }
    }

    get challengeLocationId() {
        return this.clueLocation?.challenge_location_info?._id?.$oid ?? '';
    }

    get clue_category(): TopicCategory | undefined {
        return topicData.categories.find(cat => cat._id?.$oid === this.clueLocation?.point_info?.topic_category_id?.$oid);
    }

    private updateCheckinStatus() {
        // Pending status depends on challengeLocationId (derived from clueLocation),
        // not on this.point — scavenger-hunt clue cards pass .point=undefined and
        // would otherwise never show the "Awaiting Approval" state.
        this.isCheckedin = isDefined(this.point) ? pointCache.isUserCheckedIn(this.point) : false;
        this.isPendingApproval = !this.isCheckedin
            && (challengeData.activated_challenge?.pending_checkin_location_ids?.includes(this.challengeLocationId) ?? false);

        if (isDefined(this.point)) {
            this.showSpecialOffer = true;

            if (topicData.hasChallenge) {
                const challenge = challengeData.challenge;
                if (!challenge) {
                    return;
                }
                this.showSpecialOffer = shouldRevealSpecialOffer({
                    challenge,
                    isCheckedIn: this.isCheckedin,
                    isGuide: false,
                });
            }
        }
        if (challengeData.activated_challenge) {
            challengeData.activated_challenge.scavenger_hunt_use?.hint_location_ids.forEach((hintLocationId) => {
                if (hintLocationId.$oid === this.challengeLocationId) {
                    this.showHint = true;
                }
            }
            );
        }
    }

    closePointCard() {
        topicData.mapComponent?.closeListOfPoints();
    }

    checkInClick() {
        // dispatch event to check in
        this.dispatchEvent(new CustomEvent('checkin', { detail: { challenge_location_id: this.challengeLocationId } }));
    }

    async viewHintClick(locationId: string) {
        const deduction = challengeData.challenge?.scavenger_hunt_settings?.hint_use_deduction ?? 0;
        if (deduction > 0) {
            if (!confirm(`Are you sure you want to view the ${this.hintName.toLowerCase()}? This will deduct ${deduction} points from your total possible points for this location.`)) {
                return;
            }
        }
        await challengeData.getHintForLocation(locationId);
        if (challengeData.activated_challenge) {
            challengeData.activated_challenge.scavenger_hunt_use?.hint_location_ids.forEach((hintLocationId) => {
                if (hintLocationId.$oid === this.challengeLocationId) {
                    this.showHint = true;
                }
            }
            );
        }
    }

    private onDirectionsClick() {
        let directionsMode= 'd';

        switch (topicData.topic?.type_get_directions) {
            case DirectionType.drive:
                directionsMode = 'd';
                break;
            case DirectionType.walk:
                directionsMode = 'w';
                break;
            case DirectionType.bike:
                directionsMode = 'b';
                break;
            case DirectionType.transit:
                directionsMode = 'r';
                break;
        }

        if (this.point && isValidString(this.point.location.place_id)) {
            const location = `https://www.google.com/maps?daddr=${encodeURIComponent(
                this.point?.location.search ?? ''
            )}&dirflg=${directionsMode ?? 'd'}&saddr=My+Location`;
            window.open(location, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');

            logWithGoogleTagAndTopicOrganization('Clue Card', GoogleTagEvent.POINT_GET_DIRECTIONS_CLICKED, {
                point_id: this.point?._id.$oid,
                point_name: this.point?.name,
            });
        }
    }

    async giveUpClick(locationId: string) {
        if (!confirm('Are you sure you want to give up on this location? You will not be able to earn points for this location.')) {
            return;
        }
        await challengeData.giveUpOnLocation(locationId);
        this.dispatchEvent(new CustomEvent('giveup', { detail: { challenge_location_id: this.challengeLocationId } }));
    }

    render() {
        return html`
            <div class="expanded-point-card">
                <div class="expanded-point-card__image-wrapper">
                    ${undefinedIfEmpty(this.clueLocation?.challenge_location_info?.clue_details?.image_url)
                        ? html`<img id="image_holder" src=${this.clueLocation?.challenge_location_info?.clue_details?.image_url ?? ''} class="expanded-point-card__single-image"/>`
                        : ''}
                    <div class="expanded-point-card__content">
                        <span class="mini-point-card__category-wrapper">
                            ${topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined
                                ? html`<img src="${makeColorsIntoNumberSvg(getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid), this.point?.number, getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid))}"
                                            class="mini-point-card__point-number"
                                            width="28"
                                            height="28"/>`
                                : ''
                            }
                            <span class="mini-point-card__category-pill">
                                <img src="${this.clue_category?.icon ?? ''}" width="12" height="12"/>
                                ${this.clue_category?.name}
                            </span>
                            ${this.challenge_points !== undefined
                                ? html`<span class="mini-point-card__category-pill">
                                        ${this.challenge_points} ${this.challenge_points === 1 ? 'point' : 'points'}
                                    </span>`
                                : ''
                            }
                        </span>

                        <span class="expanded-point-card__title">
                            ${this.point?.featured === true
                                ? html`<mwc-icon class="expanded-expanded-point-card__title--featured">star</mwc-icon>`
                                : ''}
                            ${isValidString(this.clueLocation?.challenge_location_info?.clue_details?.clue)
                                ? html`<h2 class="expanded-expanded-point-card__title-text">${this.clueLocation?.challenge_location_info?.clue_details?.clue ?? ''}</h2>`
                                : ''}
                        </span>
                        ${isValidString(this.eventString) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.date_time?.hide_on_point_card, false)
                            ? html`
                                  <div class="expanded-point-card__event">
                                      <mwc-icon class="expanded-point-card__event-icon">calendar_month</mwc-icon>
                                      ${this.eventString}
                                  </div>`
                            : ''}
                        ${this.showHint && isDefined(undefinedIfEmpty(this.clueLocation?.challenge_location_info?.clue_details?.hint)) ? html`
                            ${isValidString(this.clueLocation?.challenge_location_info?.clue_details?.hint)
                                ? html`<div class="expanded-point-card__hint">
                                    <h4 class="hint-header"><mwc-icon class="admin-info-icon">lightbulb</mwc-icon>${this.hintName}</h4>
                                    <div>${this.clueLocation?.challenge_location_info?.clue_details?.hint}</div>
                                </div>`
                                : ''}
                        ` : ''}

                    ${challengeData.challenge?.allow_internal_notes === true ? html`
                        <div class="internal-notes-toggle" @click="${this.onToggleComments}">
                            <mwc-icon class="admin-info-icon">note</mwc-icon>
                            <span>${this.showComments ? 'Hide' : 'View'} Internal Notes ${this.allComments.length > 0 ? `(${this.allComments.length})` : ''}</span>
                        </div>
                    ` : ''}

                    ${this.showComments ? html`
                        <div class="expanded-point-card__form">
                            <comment-component
                                @close=${this.onToggleComments}
                                @comment-changed=${async () => await this.commentChanged()}
                                .allComments=${this.allComments}
                                .entity_id=${this.clueLocation?.point_info?._id.$oid ?? ''}
                                .entity_type=${EntityType.Point}>
                            </comment-component>
                        </div>
                    ` : ''}

                    </div>
                    <div class="action-buttons">
                        ${!this.showHint && isDefined(this.clueLocation?.challenge_location_info?.clue_details?.hint) ? html`
                            <form-button
                                variant="outline"
                                icon="lightbulb"
                                ?fullWidth=${true}
                                @click=${async () => await this.viewHintClick(this.challengeLocationId)}
                            >
                                <translated-span>View ${this.hintName}</translated-span>
                            </form-button>
                        ` : challengeData.isType(ChallengeType.Treasure) ? '' : html`
                            <form-button
                                variant="outline"
                                icon="wrong_location"
                                ?fullWidth=${true}
                                @click=${async () => await this.giveUpClick(this.challengeLocationId)}
                            >
                                <translated-span>Give Up</translated-span>
                            </form-button>
                        `}
                        ${challengeData.isType(ChallengeType.Treasure) ? html` <!-- Treasure type direction buton for high street -->
                            <form-button
                                icon="directions"
                                ?fullWidth=${true}
                                @click=${this.onDirectionsClick}
                                ?disabled=${this.isCheckedin}
                                style="flex: 1"
                            >
                                <translated-span>${topicData.topic?.text_get_directions ?? 'Directions'}</translated-span>
                            </form-button>
                        ` : ''}
                        <form-button
                            icon=${this.isPendingApproval ? 'hourglass_top' : 'star'}
                            ?fullWidth=${true}
                            @click=${() => this.checkInClick()}
                            ?disabled=${this.isCheckedin || this.isPendingApproval}
                            style="flex: 1"
                        >
                            <translated-span>${this.isPendingApproval ? 'Awaiting Approval' : (challengeData.challenge?.button_text ?? 'Check In')}</translated-span>
                        </form-button>
                </div>
            </div>

            <mwc-dialog class="dialog" hideActions>
                <modal-dialog size="lg">Add To Map</modal-dialog>
            </mwc-dialog>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    get clueName() {
        if (challengeData.isType(ChallengeType.Hunt) || challengeData.isType(ChallengeType.Trivia)) {
            return 'Clue';
        } else if (challengeData.isType(ChallengeType.Treasure)) {
            return 'Stop Name';
        }
        return 'Clue';
    }

    get hintName() {
        if (challengeData.isType(ChallengeType.Hunt) || challengeData.isType(ChallengeType.Trivia)) {
            return 'Hint';
        } else if (challengeData.isType(ChallengeType.Treasure)) {
            return 'Clue';
        }
        return 'Hint';
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-typography-subtitle1-font-size: 14px;
                --mdc-icon-button-size: 32px;
                width: 100%;
            }

            /* width */
            .expanded-point-card__content::-webkit-scrollbar, .expanded-point-card__content--horizontal::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .expanded-point-card__content::-webkit-scrollbar-track, .expanded-point-card__content--horizontal::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .expanded-point-card__content::-webkit-scrollbar-thumb, .expanded-point-card__content--horizontal::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .expanded-point-card__content {
                padding: 8px 16px;
                flex: 1 1 auto;
                overflow: hidden auto;
                font-size: .75rem;
            }

            .expanded-point-card {
                width: 100%;
                height: 100%;
                display: flex;
                background: white;
                z-index: 1;
                flex-direction: column;
            }

            .expanded-point-card__hint {
                border: 1px solid var(--main-featured-color);
                background: var(--main-sand-color);
                border-radius: 8px;
                padding: 8px;
                margin-top: 8px;
            }

            .admin-info-icon {
                --mdc-icon-size: 16px;
                color: var(--main-featured-color);
            }

            .expanded-point-card--discovery {
                background: transparent;
                border: none;
                border-bottom: 0.5px solid var(--theme-input-border-color);
                word-wrap: break-word;
                margin-left: 8px;
            }

            .no-bottom-border {
                border-bottom: none;
            }

            .expanded-point-card__discovery-content {
                float: left;
                width: 100%;
            }

            .expanded-point-card__content--discovery {
                display: flex;
                margin: 4x 8px;
                justify-content: space-between;
                flex-direction: column;
            }

            .expanded-point-card__content--list-of-points {
                display: flex;
                margin: 0;
                justify-content: space-between;
                flex-direction: column;
            }

            .emailInputForm {
                flex: 1;
            }

            .expanded-point-card__single-image {
                height: 30%;
                width: calc(100% - 24px);
                border-radius: 8px;
                object-fit: cover;
                margin: 12px;
            }

            .expanded-point-card--horizontal {
                width: 100%;
                display: flex;
                flex-direction: column;
                max-height: calc(90dvh - ${unsafeCSS(heights.nav)} - 50px);
            }

            .expanded-point-card__form {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                margin-top: 10px;
                gap: 10px;
                background-color: var(--theme-light-scrollbar-color);
                padding: 1rem;
            }

            .expanded-point-card--backpack {
                width: 100%;
                display: flex;
                flex-direction: column;
                height: 300px;
            }

            .expanded-point-card__content--horizontal .expanded-expanded-point-card__title-text {
                font-size: 18px;
                align-self: center;
            }

            .expanded-point-card__point-number {
                margin-right: -5px;
                z-index: 2;
            }

            .expanded-point-card__custom-icon {
                margin-right: -5px;
                z-index: 1;
                border-radius: 100%;
            }

            .expanded-point-card__checked-in {
                margin-right: -5px;
                z-index: 3;
            }

            .expanded-point-card__special-offer {
                border: 1px dashed var(--theme-input-color);
                padding: 4px;
                margin-top: 8px;
                display: inline-block;
            }

            .expanded-point-card__menu-wrapper {
                display: flex;
                align-items: center;
                position: absolute;
                top: 4px;
                right: 0;
                z-index: 2;
            }

            .expanded-point-card__edit,
            .expanded-point-card__info,
            .expanded-point-card__close_admin {
                background-color: var(--main-featured-color);
                margin-right: 4px;
                border-radius: 4px;
                border:none;
                --mdc-theme-on-primary: var(--theme-input-color);
            }

            .expanded-point-card__upvote,
            .expanded-point-card__share,
            .expanded-point-card__close_cc {
                background-color: white;
                box-shadow: rgb(31 27 45 / 12%) 0px 2px 8px -4px, rgb(31 27 45 / 12%) 0px 4px 16px;
                --mdc-icon-size: 18px;
                border-radius: 40px;
                margin-right: 6px;
                --mdc-icon-button-size: 30px;
            }

            .expanded-point-card__upvote--user-upvoted {
                background-color: var(--theme-input-color);
                color: white;
            }

            .expanded-point-card__category-wrapper {
                display: flex;
                align-items: center;
                position: absolute;
                bottom: 0;
                background: white;
                padding: 0 10px;
            }

            .expanded-point-card__booking {
                display: flex;
                flex-direction: row;
                flex-wrap: none;
                overflow-x: scroll;
                justify-content: flex-start;
                gap: 5px;
                color: var(--theme-input-color);
            }

            .expanded-point-card__booking::-webkit-scrollbar {
                display: none;
            }

            .expanded-point-card__associated-map {
                color: var(--theme-input-color);
                border-radius: 9px;
                background: var(--theme-light-scrollbar-color);
                padding: 2px 6px;
                border: 1px solid var(--theme-input-border-color);
                font-weight: bold;
                font-size: 12px;
                letter-spacing: 0.5px;
                text-decoration: none;
                cursor: pointer;
                white-space: nowrap;
                overflow: hidden;
                max-width: 300px;
                display: inline-block;
                text-overflow: ellipsis;
            }

            .expanded-point-card__associated-wrapper {
                padding-top: 10px;
            }

            .expanded-point-card__image-wrapper {
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .expanded-point-card__image-wrapper--no-image {
                position: static;
                display: flex;
                justify-content: space-between;
                padding: 4px 0px;
            }

            .expanded-point-card__image-wrapper--no-image .expanded-point-card__category-wrapper {
                position: static;
            }

            .expanded-point-card__image-wrapper--no-image .expanded-point-card__menu-wrapper {
                position: static;
            }

            .expanded-expanded-point-card__title-text {
                font-weight: 700;
                font-size: 1rem;
                margin-bottom: 5px;
                color: var(--main-primary-color);
            }

            .expanded-point-card__source {
                color: var(--theme-input-color);
                border-radius: 9px;
                background: var(--theme-light-scrollbar-color);
                padding: 2px 6px;
                border: 1px solid var(--theme-input-border-color);
                font-weight: bold;
                font-size: 12px;
                letter-spacing: 0.5px;
                text-decoration: none;
            }

            .expanded-point-card__source-wrapper {
                padding-top: 10px;
            }

            .expanded-point-card__address {
                color: var(--theme-input-color);
            }

            .expanded-point-card__address-icon,
            .expanded-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 14px;
                width: 14px;
            }

            .expanded-point-card__event {
                color: var(--theme-input-color);
            }

            .expanded-point-card__description {
                color: var(--theme-input-color);
            }

            .expanded-expanded-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .expanded-point-card__title {
                display: flex;
            }

            .point-card__tags-wrapper {
                display: flex;
                flex-wrap: wrap;
                padding: 8px 0;
            }

            .expanded-point-card__tag {
                user-select: none;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 4px 8px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid lightgray;
                background: #fff;
                filter: drop-shadow(rgba(0, 0, 0, 0.25) 0px 1px 1px);
            }

            .expanded-point-card__tag-icon {
                --mdc-icon-size: 16px;
                padding-right: 4px;
            }

            .small-text {
                font-size: 0.75rem;
                line-height: 0.75rem;
            }

            .admin-action-list-holder {
                margin-bottom: 5px;
                margin-top: 0;
            }

            .admin-actions-list {
                padding: 5px;
                background-color: var(--main-featured-color);
                margin: 5px;
                border-radius: 5px;
                cursor: pointer;
                white-space: nowrap;

            }
            .hidden {
                display: none;
            }

            .mini-point-card__category-wrapper {
                display: flex;
                gap: 5px;
            }

            .hint-header {
                margin-top: 0;
                margin-bottom: 5px;
                display: flex;
                align-items: center;
            }

            .mini-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 2px 6px;
                gap: 3px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .mini-point-card__image-wrapper {
                display: flex;
                width: 25%;
                background-color: var(--theme-white-color);
                overflow: hidden;
                align-items: center;
                opacity: 0.98;
            }

            .wrapper-icon {
                background-color: var(--theme-border-color);
                justify-content: center;
            }

            .space-wrapper {
                flex: 1;
                background-color: var(--theme-white-color);
            }

            .internal-notes-toggle {
                display: flex;
                align-items: center;
                gap: 5px;
                cursor: pointer;
                color: var(--main-primary-color);
                padding: 10px 0;
                font-size: 14px;
            }

            .internal-notes-toggle:hover {
                text-decoration: underline;
            }

            .action-buttons {
                display: flex;
                flex-wrap: wrap;
                margin-top: 10px;
                gap: 10px;
            }

            @media only screen and (max-width: 550px) {
                .expanded-point-card--horizontal_admin {
                    max-height: calc(
                        90dvh - ${unsafeCSS(heights.nav)} -
                            ${unsafeCSS(heights.mobileSettingsControls)} - 125px
                    );
                    display: flex;
                    flex-direction: column;
                }

                .expanded-point-card--horizontal_cc {
                    max-height: calc(90dvh - ${unsafeCSS(heights.nav)} - 75px);
                    display: flex;
                    flex-direction: column;
                }

                .expanded-point-card--discovery {
                    max-height: none;
                    display: block;
                }

                .expanded-point-card__content--horizontal {
                    margin-top: 5px;
                    margin-bottom: 5px;
                    overflow: hidden auto;
                    flex: 1 1 auto;
                }
            }
        `;
    }
}
